import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface TwoFactorContextType {
  isVerified: boolean;
  setVerified: (verified: boolean) => void;
}

const TwoFactorContext = createContext<TwoFactorContextType>({
  isVerified: false,
  setVerified: () => {},
});

export const useTwoFactor = () => useContext(TwoFactorContext);

// Routes that don't require 2FA check
const EXCLUDED_ROUTES = [
  "/",
  "/login",
  "/2fa-verify",
  "/2fa-setup-required",
  "/reset-password",
  "/uitnodiging",
  "/aanbeveling",
  "/aanbeveling-goedkeuren",
  "/linkedin-callback",
  "/voorwaarden",
  "/disclaimer",
  "/veiligheid",
  "/privacy",
  "/hoe-werkt-het",
  "/voor-zorgverleners",
  "/voor-clienten",
  "/vacatures",
  "/zorgverlener",
  "/zoek-zorgverlener",
  "/hulpvraag",
  "/zorgsoorten",
  "/nederland",
  "/belgie",
  "/duitsland",
  "/contact",
  "/gemeente-samenwerking",
  "/gratis-hulpvraag-plaatsen",
  "/pgb-zorgverleners-vinden",
  "/abonnementen",
  "/affiliate-voorwaarden",
];

interface TwoFactorGuardProps {
  children: React.ReactNode;
}

export const TwoFactorGuard = ({ children }: TwoFactorGuardProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check2FARequirement = async () => {
      // Skip if no user or still loading
      if (loading) return;
      
      if (!user) {
        setChecking(false);
        return;
      }

      // Check if current route is excluded
      const isExcluded = EXCLUDED_ROUTES.some(route => 
        location.pathname === route || location.pathname.startsWith(route + "/")
      );

      if (isExcluded) {
        setChecking(false);
        return;
      }

      try {
        // Fetch user type info
        const userType = user.user_metadata?.user_type;
        
        // Check if admin
        const { data: isAdmin } = await supabase.rpc('has_role', { 
          _user_id: user.id, 
          _role: 'admin' 
        });

        // Check caregiver profile for B2B status
        const { data: caregiverProfile } = await supabase
          .from('caregiver_profiles')
          .select('caregiver_type')
          .eq('user_id', user.id)
          .maybeSingle();

        const isB2B = caregiverProfile?.caregiver_type === 'bedrijf';
        const isCaregiver = userType === 'caregiver' && !isB2B;
        const isClient = userType === 'client';

        // Fetch 2FA requirements from admin_settings
        const { data: twoFaSettings } = await supabase
          .from('admin_settings')
          .select('setting_key, setting_value')
          .in('setting_key', [
            'require_2fa_admin',
            'require_2fa_caregiver', 
            'require_2fa_client', 
            'require_2fa_b2b'
          ]);

        const settings: Record<string, boolean> = {
          require_2fa_admin: false,
          require_2fa_caregiver: false,
          require_2fa_client: false,
          require_2fa_b2b: true, // Default true for B2B
        };

        twoFaSettings?.forEach((s) => {
          settings[s.setting_key] = s.setting_value === 'true';
        });

        // Determine if 2FA is required for this user
        const requires2FA = 
          (isAdmin && settings.require_2fa_admin) ||
          (isB2B && settings.require_2fa_b2b) ||
          (isCaregiver && settings.require_2fa_caregiver) ||
          (isClient && settings.require_2fa_client);

        if (!requires2FA) {
          // No 2FA required, user is verified by default
          setIsVerified(true);
          setChecking(false);
          return;
        }

        // 2FA is required - check if user has it enabled
        const { data: totpData } = await supabase
          .from('user_totp_secrets')
          .select('is_enabled, last_verified_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!totpData?.is_enabled) {
          // User needs to set up 2FA
          navigate('/2fa-setup-required', { replace: true });
          return;
        }

        // Server-side bron van waarheid: last_verified_at uit user_totp_secrets.
        // sessionStorage wordt NIET vertrouwd — anders kan een gebruiker via de
        // browser console de sleutel zetten en 2FA omzeilen.
        if (totpData.last_verified_at) {
          const lastVerified = new Date(totpData.last_verified_at);
          const hoursSinceVerification = (Date.now() - lastVerified.getTime()) / (1000 * 60 * 60);

          if (hoursSinceVerification < 24) {
            setIsVerified(true);
            setChecking(false);
            return;
          }
        }

        // 2FA verificatie vereist
        const currentPath = location.pathname;
        navigate(`/2fa-verify?redirect=${encodeURIComponent(currentPath)}`, { replace: true });

      } catch (error) {
        console.error('Error checking 2FA requirements:', error);
        setChecking(false);
      }
    };

    check2FARequirement();
  }, [user, loading, location.pathname, navigate]);

  // Markeer 2FA als geverifieerd (aangeroepen vanuit TwoFactorVerify pagina).
  // We slaan dit NIET op in sessionStorage als enige bron — de echte verificatie
  // wordt server-side opgeslagen via last_verified_at in user_totp_secrets.
  const handleSetVerified = (verified: boolean) => {
    setIsVerified(verified);
  };

  return (
    <TwoFactorContext.Provider value={{ isVerified, setVerified: handleSetVerified }}>
      {children}
    </TwoFactorContext.Provider>
  );
};

export default TwoFactorGuard;
